import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { ImageCropUploader } from '../common/ImageCropUploader';
import { CATEGORY_ICONS, CATEGORY_COLORS, CategoryIcon, DEFAULT_CATEGORY_ICON, DEFAULT_CATEGORY_COLOR } from '../../lib/categoryIcons';
import { useConfirm } from '../../context/ConfirmContext';
import { Plus, Pencil, Trash2, X, Check, Eye, EyeOff, Layers } from 'lucide-react';

const blankSub = () => ({ name: '', icon: DEFAULT_CATEGORY_ICON, color: DEFAULT_CATEGORY_COLOR, image: null, image_x: 50, image_y: 50, image_zoom: 1, published: true });

/** Gestione sottocategorie di una categoria (nome + icona/colore + immagine). */
export const SubcategoryManagerDialog = ({ open, onOpenChange, category, subcategories = [], onChanged }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [form, setForm] = useState(blankSub());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm(blankSub()); setEditingId(null); } }, [open, category]);

  const reset = () => { setForm(blankSub()); setEditingId(null); };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, icon: s.icon || DEFAULT_CATEGORY_ICON, color: s.color || DEFAULT_CATEGORY_COLOR, image: s.image || null, image_x: s.image_x ?? 50, image_y: s.image_y ?? 50, image_zoom: s.image_zoom ?? 1, published: s.published !== false });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('menu.nameRequired')); return; }
    if (!category) return;
    setSaving(true);
    const payload = { category_id: category.id, ...form, published: form.published !== false };
    try {
      if (editingId) await api.put(`/subcategories/${editingId}`, payload);
      else await api.post('/subcategories', payload);
      toast.success(t('subcat.saved'));
      reset();
      onChanged && onChanged();
    } catch (e) { toast.error(t('menu.saveError')); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (await confirm({ title: t('subcat.deleteConfirm', { name: s.name }), destructive: true, confirmText: t('menu.delete') })) {
      await api.delete(`/subcategories/${s.id}`);
      toast.success(t('subcat.deleted'));
      if (editingId === s.id) reset();
      onChanged && onChanged();
    }
  };

  const togglePublish = async (s) => {
    await api.patch(`/subcategories/${s.id}/publish`, null, { params: { published: !(s.published !== false) } });
    onChanged && onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="subcategory-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> {t('subcat.title', { name: category?.name || '' })}</DialogTitle>
          <DialogDescription>{t('subcat.hint')}</DialogDescription>
        </DialogHeader>

        {/* Elenco sottocategorie */}
        <div className="space-y-2">
          {subcategories.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t('subcat.none')}</div>
          )}
          {subcategories.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border p-2.5" data-testid={`subcategory-row-${s.id}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${s.color || DEFAULT_CATEGORY_COLOR}1a` }}>
                <CategoryIcon iconKey={s.icon} color={s.color} className="h-5 w-5" />
              </span>
              <span className="flex-1 font-medium truncate">{s.name}</span>
              {s.published === false && <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"><EyeOff className="h-3 w-3" /> {t('menu.outOfMenu')}</span>}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => togglePublish(s)} title={t('menu.showHideDigital')}>
                {s.published !== false ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => edit(s)} data-testid={`subcategory-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(s)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>

        {/* Form nuova/modifica */}
        <div className="mt-2 rounded-xl border border-border p-4 space-y-3">
          <div className="font-semibold text-sm">{editingId ? t('subcat.edit') : t('subcat.new')}</div>
          <div>
            <Label className="mb-1.5 block">{t('menu.name')}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('subcat.namePlaceholder')} data-testid="subcategory-name-input" />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('menu.categoryLogo')}</Label>
            <div className="grid grid-cols-8 gap-1.5 rounded-xl border border-border p-2 max-h-36 overflow-y-auto">
              {CATEGORY_ICONS.map(({ key, label, Icon }) => {
                const active = form.icon === key;
                return (
                  <button key={key} type="button" title={label} aria-label={label} aria-pressed={active}
                    onClick={() => setForm({ ...form, icon: key })}
                    className={`flex h-9 w-full items-center justify-center rounded-lg border transition-colors ${active ? 'border-transparent ring-2 ring-offset-1 ring-offset-background' : 'border-border hover:bg-muted'}`}
                    style={active ? { backgroundColor: `${form.color}1a`, '--tw-ring-color': form.color } : undefined}>
                    <Icon className="h-4 w-4" style={{ color: form.color }} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block">{t('menu.logoColor')}</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {CATEGORY_COLORS.map((col) => (
                <button key={col} type="button" aria-label={col} onClick={() => setForm({ ...form, color: col })}
                  className="flex h-7 w-7 items-center justify-center rounded-full transition-transform hover:scale-110"
                  style={{ backgroundColor: col, boxShadow: form.color === col ? `0 0 0 2px var(--background), 0 0 0 4px ${col}` : 'none' }}>
                  {form.color === col && <Check className="h-3.5 w-3.5 text-white" />}
                </button>
              ))}
              <label className="relative ml-1 flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-border px-2 text-xs text-muted-foreground hover:bg-muted">
                <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: form.color }} />
                {t('menu.other')}
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="absolute inset-0 cursor-pointer opacity-0" />
              </label>
            </div>
          </div>

          <ImageCropUploader value={form} onChange={setForm} aspect="16 / 9" label={t('menu.backgroundImage')} hint={t('menu.backgroundHint')} testId="subcategory-image-uploader" />

          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div className="text-sm font-medium">{t('menu.showInDigital')}</div>
            <Switch checked={form.published !== false} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="subcategory-publish-switch" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="subcategory-save-button">{editingId ? t('menu.update') : t('menu.add')}</Button>
            {editingId && <Button variant="ghost" className="rounded-xl" onClick={reset}><X className="h-4 w-4 mr-1" /> {t('menu.cancel')}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
